import React, { useState } from 'react'
import { Navigate, useLocation, Link } from 'react-router-dom'
import { ShieldCheck, Loader2 } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useBranding } from '../context/BrandingContext'

const AUTH_STYLES = `
body { overflow: auto; font-family: var(--font); background: var(--bg-0); color: var(--text); }
.auth-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  background:
    radial-gradient(1200px 800px at 0% -10%, rgba(124, 92, 255, 0.12), transparent 60%),
    radial-gradient(900px 600px at 110% 120%, rgba(91, 124, 255, 0.10), transparent 60%),
    var(--bg-0);
}
[data-theme="light"] .auth-page {
  background:
    radial-gradient(1200px 800px at 0% -10%, rgba(124, 92, 255, 0.07), transparent 60%),
    radial-gradient(900px 600px at 110% 120%, rgba(91, 124, 255, 0.06), transparent 60%),
    var(--bg-0);
}
.auth-showcase {
  position: relative; overflow: hidden; display: flex;
  flex-direction: column; padding: 44px 56px;
  border-right: 1px solid var(--border);
}
.showcase-grid {
  position: absolute; inset: 0;
  background-image: linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px);
  background-size: 46px 46px;
  mask-image: radial-gradient(900px 700px at 30% 25%, #000 0%, transparent 78%);
  -webkit-mask-image: radial-gradient(900px 700px at 30% 25%, #000 0%, transparent 78%);
  opacity: 0.6; pointer-events: none;
}
.showcase-orb {
  position: absolute; width: 460px; height: 460px; border-radius: 50%;
  filter: blur(20px);
  background: radial-gradient(circle at 50% 50%, var(--accent-soft), transparent 70%);
  top: -120px; right: -120px; pointer-events: none;
}
.brand-mark { position: relative; display: flex; align-items: center; gap: 11px; }
.brand-logo {
  width: 38px; height: 38px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #A78BFA, #5B7CFF);
  box-shadow: 0 8px 24px -10px var(--accent-glow);
}
.brand-wordmark { font-size: 17px; font-weight: 600; letter-spacing: -0.02em; color: var(--text); }
.brand-wordmark span { color: var(--accent-3); }
.showcase-body { position: relative; margin-top: auto; max-width: 460px; }
.showcase-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 500; letter-spacing: 0.09em; text-transform: uppercase;
  color: var(--text-muted); padding: 5px 11px 5px 8px;
  border: 1px solid var(--border); background: var(--surface); border-radius: 999px;
}
.showcase-headline {
  margin: 22px 0 0; font-size: 40px; line-height: 1.08;
  font-weight: 600; letter-spacing: -0.03em; color: var(--text);
}
.showcase-headline em {
  font-style: normal;
  background: linear-gradient(120deg, var(--accent-3), var(--accent-2));
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.showcase-sub { margin-top: 16px; color: var(--text-muted); font-size: 15px; line-height: 1.6; max-width: 420px; }
.showcase-features { display: flex; flex-direction: column; gap: 14px; margin-top: 34px; }
.showcase-feat { display: flex; align-items: center; gap: 13px; font-size: 13.5px; color: var(--text-soft); }
.feat-ico {
  width: 30px; height: 30px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  background: var(--accent-soft); color: var(--accent-3);
  border: 1px solid var(--border); flex-shrink: 0;
}
.showcase-foot {
  position: relative; margin-top: 40px; padding-top: 22px;
  border-top: 1px solid var(--border); display: flex; align-items: center; gap: 16px;
}
.showcase-quote { font-size: 13px; color: var(--text-muted); line-height: 1.5; }
.showcase-quote b { color: var(--text-soft); font-weight: 600; }
.auth-form-wrap {
  position: relative; display: flex; align-items: center;
  justify-content: center; padding: 40px;
}
.auth-card { width: 100%; max-width: 388px; }
@media (max-width: 940px) {
  .auth-page { grid-template-columns: 1fr; }
  .auth-showcase { display: none; }
}
@media (max-width: 480px) {
  .auth-form-wrap { padding: 24px 18px; }
}
`

// Brand lockup shown on the auth showcase / cards. Uses the uploaded logo when
// present (the dark-surface variant, since the showcase is always dark), and the
// configured CMS name as the wordmark.
const BrandMark = ({ branding }) => {
  const logoSrc = branding.logoUrl || branding.logoUrlLight
  return (
    <div className="brand-mark">
      <div className="brand-logo">
        {logoSrc
          ? <img src={logoSrc} alt={branding.cmsName} style={{ width: 22, height: 22, objectFit: 'contain' }} />
          : (
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M8 8.5c1.6-2 6.4-2 8 0M8 12c1.6-2 6.4-2 8 0M8 15.5c1.6-2 6.4-2 8 0" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          )}
      </div>
      <div className="brand-wordmark">{branding.cmsName}</div>
    </div>
  )
}

const Login = () => {
  const { login, completeForcedMfaLogin, user } = useAuth()
  const { toggleTheme } = useTheme()
  const branding = useBranding()
  const location = useLocation()

  const [email, setEmail] = useState(() => localStorage.getItem('loginEmail') || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [remember, setRemember] = useState(() => localStorage.getItem('rememberStaySignedIn') === 'true')

  // 2FA TOTP step
  const [totpStep, setTotpStep] = useState(false)
  const [totpToken, setTotpToken] = useState('')
  const [totpError, setTotpError] = useState('')
  const [pendingCredentials, setPendingCredentials] = useState(null)

  // Forced MFA setup step
  const [mfaSetupStep, setMfaSetupStep] = useState(false) // false | 'loading' | 'scan'
  const [mfaSetupToken, setMfaSetupToken] = useState(null)
  const [mfaSetupQr, setMfaSetupQr] = useState('')
  const [mfaSetupSecret, setMfaSetupSecret] = useState('')
  const [mfaSetupCode, setMfaSetupCode] = useState('')
  const [mfaSetupError, setMfaSetupError] = useState('')

  const submittingRef = React.useRef(false)

  // Persist email as user types
  React.useEffect(() => {
    if (email) localStorage.setItem('loginEmail', email)
  }, [email])

  // Load saved error on mount
  React.useEffect(() => {
    const savedError = localStorage.getItem('loginError')
    if (savedError) {
      const timer = setTimeout(() => {
        setError(savedError)
        localStorage.removeItem('loginError')
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [])

  // Redirect if already logged in
  if (user) {
    const from = location.state?.from?.pathname || (user.role === 'admin' ? '/admin' : '/client')
    return <Navigate to={from} replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (loading || submittingRef.current) return

    submittingRef.current = true
    setLoading(true)
    setError('')
    localStorage.removeItem('loginError')

    try {
      const result = await login(email, password, remember)

      if (result.requires2FA) {
        setPendingCredentials({ email, password })
        setTotpStep(true)
        return
      }

      if (result.requiresMfaSetup) {
        setMfaSetupToken(result.setupToken)
        setMfaSetupStep('loading')
        try {
          const resp = await fetch('/api/auth/2fa/setup', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${result.setupToken}`, 'Content-Type': 'application/json' }
          })
          const data2fa = await resp.json()
          setMfaSetupQr(data2fa.qrCode)
          setMfaSetupSecret(data2fa.secret)
          setMfaSetupStep('scan')
        } catch {
          setMfaSetupStep(false)
          setError('Failed to start MFA setup. Please try again.')
        }
        return
      }

      if (!result.success) {
        let errorMessage = result.error
        if (errorMessage === 'Login failed' || errorMessage === 'Invalid email or password') {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.'
        } else if (errorMessage?.includes('Too many')) {
          errorMessage = 'Too many login attempts. Please wait a moment and try again.'
        }
        setError(errorMessage)
        setPassword('')
      } else {
        localStorage.removeItem('loginEmail')
        localStorage.removeItem('loginError')
      }
    } catch (err) {
      console.error('Login error:', err)
      setError('Something went wrong. Please try again.')
      setPassword('')
    } finally {
      setLoading(false)
      submittingRef.current = false
    }
  }

  async function onSubmitTotp(e) {
    e.preventDefault()
    if (!totpToken || totpToken.length !== 6) return
    setLoading(true)
    setTotpError('')
    try {
      const result = await login(pendingCredentials.email, pendingCredentials.password, remember, totpToken)
      if (!result.success) {
        setTotpError(result.error || 'Invalid code. Please try again.')
        setTotpToken('')
      } else {
        localStorage.removeItem('loginEmail')
      }
    } catch {
      setTotpError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function onSubmitMfaSetup(e) {
    e.preventDefault()
    if (!mfaSetupCode || mfaSetupCode.length !== 6) return
    setLoading(true)
    setMfaSetupError('')
    try {
      const resp = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${mfaSetupToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: mfaSetupCode })
      })
      const data2fa = await resp.json()
      if (!resp.ok) {
        setMfaSetupError(data2fa.error || 'Invalid code. Please try again.')
        setMfaSetupCode('')
      } else if (data2fa.loginComplete) {
        localStorage.removeItem('loginEmail')
        localStorage.removeItem('loginError')
        completeForcedMfaLogin(data2fa.token, data2fa.user)
      }
    } catch {
      setMfaSetupError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── Forced MFA setup UI ──
  if (mfaSetupStep) {
    return (
      <>
        <style>{AUTH_STYLES}</style>
        <div className="auth-page">
          <section className="auth-showcase">
            <div className="showcase-grid" />
            <div className="showcase-orb" />
            <BrandMark branding={branding} />
          </section>
          <section className="auth-form-wrap">
            <div className="auth-card">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 40 }}>
                <button className="ds-btn ds-btn-icon ds-btn-ghost" onClick={toggleTheme} title="Toggle theme" type="button">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19"/>
                  </svg>
                </button>
              </div>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #A78BFA, #5B7CFF)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <ShieldCheck style={{ width: 24, height: 24, color: 'white' }} />
                </div>
                <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.025em', margin: 0, color: 'var(--text)' }}>Set Up Two-Factor Authentication</h1>
                <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Your administrator requires MFA. Set it up now to continue.</p>
              </div>

              {mfaSetupStep === 'loading' && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                  <Loader2 style={{ width: 32, height: 32, animation: 'spin 1s linear infinite', color: 'var(--accent)' }} />
                </div>
              )}

              {mfaSetupStep === 'scan' && (
                <form onSubmit={onSubmitMfaSetup}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>
                      Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                    </p>
                    {mfaSetupQr && (
                      <img src={mfaSetupQr} alt="MFA QR Code" style={{ width: 176, height: 176, borderRadius: 12, border: '1px solid var(--border)', background: 'white', padding: 8 }} />
                    )}
                    <details style={{ width: '100%' }}>
                      <summary style={{ fontSize: 12, color: 'var(--text-faint)', cursor: 'pointer', textAlign: 'center' }}>
                        Can't scan? Enter code manually
                      </summary>
                      <p style={{ marginTop: 8, fontSize: 12, fontFamily: 'monospace', textAlign: 'center', wordBreak: 'break-all', background: 'var(--surface)', borderRadius: 8, padding: '8px 12px', border: '1px solid var(--border)', color: 'var(--text-soft)' }}>
                        {mfaSetupSecret}
                      </p>
                    </details>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 7, textAlign: 'center' }}>
                      Enter the 6-digit code to confirm
                    </label>
                    <input
                      className="ds-input"
                      type="text"
                      inputMode="numeric"
                      value={mfaSetupCode}
                      onChange={(e) => setMfaSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      autoFocus
                      maxLength={6}
                      style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.2em', fontFamily: 'monospace', height: 52 }}
                    />
                  </div>
                  {mfaSetupError && (
                    <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
                      {mfaSetupError}
                    </div>
                  )}
                  <button
                    className="ds-btn ds-btn-primary"
                    type="submit"
                    disabled={loading || mfaSetupCode.length !== 6}
                    style={{ width: '100%', height: 44, fontSize: 14, fontWeight: 600, borderRadius: 11, opacity: (loading || mfaSetupCode.length !== 6) ? 0.5 : 1 }}
                  >
                    {loading ? 'Verifying…' : 'Verify & Sign In'}
                  </button>
                </form>
              )}
            </div>
          </section>
        </div>
      </>
    )
  }

  // ── 2FA TOTP UI ──
  if (totpStep) {
    return (
      <>
        <style>{AUTH_STYLES}</style>
        <div className="auth-page">
          <section className="auth-showcase">
            <div className="showcase-grid" />
            <div className="showcase-orb" />
            <BrandMark branding={branding} />
          </section>
          <section className="auth-form-wrap">
            <div className="auth-card">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 40 }}>
                <button className="ds-btn ds-btn-icon ds-btn-ghost" onClick={toggleTheme} title="Toggle theme" type="button">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19"/>
                  </svg>
                </button>
              </div>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #A78BFA, #5B7CFF)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                </div>
                <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.025em', margin: 0, color: 'var(--text)' }}>Two-Factor Authentication</h1>
                <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Enter the 6-digit code from your authenticator app.</p>
              </div>
              <form onSubmit={onSubmitTotp}>
                <div style={{ marginBottom: 16 }}>
                  <input
                    className="ds-input"
                    type="text"
                    inputMode="numeric"
                    value={totpToken}
                    onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoFocus
                    maxLength={6}
                    style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.2em', fontFamily: 'monospace', height: 52 }}
                  />
                </div>
                {totpError && (
                  <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
                    {totpError}
                  </div>
                )}
                <button
                  className="ds-btn ds-btn-primary"
                  type="submit"
                  disabled={loading || totpToken.length !== 6}
                  style={{ width: '100%', height: 44, fontSize: 14, fontWeight: 600, borderRadius: 11, opacity: (loading || totpToken.length !== 6) ? 0.5 : 1, marginBottom: 12 }}
                >
                  {loading ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={() => { setTotpStep(false); setTotpToken(''); setTotpError('') }}
                  style={{ width: '100%', fontSize: 13, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0' }}
                >
                  ← Back to login
                </button>
              </form>
            </div>
          </section>
        </div>
      </>
    )
  }

  // ── Main login UI ──
  return (
    <>
      <style>{AUTH_STYLES}</style>
      <div className="auth-page">
        {/* Left: Brand showcase */}
        <section className="auth-showcase">
          <div className="showcase-grid" />
          <div className="showcase-orb" />

          {/* Brand */}
          <BrandMark branding={branding} />

          <div className="showcase-body">
            <div className="showcase-eyebrow">
              <span className="dot-live" />
              A calmer way to manage your site
            </div>
            <h2 className="showcase-headline">
              Your website, beautifully <em>under control</em>.
            </h2>
            <p className="showcase-sub">
              Edit content, request changes, watch your traffic and handle billing — all from one secure, considered workspace.
            </p>

            <div className="showcase-features">
              {[
                { text: 'Edit text & images without breaking the design', icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4v16h16v-7"/><path d="m14.5 5.5 4 4L11 17l-4 1 1-4 7.5-8.5Z"/></svg> },
                { text: 'Request changes and follow them through to live', icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> },
                { text: 'Domains, analytics and billing in a single view', icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19V5M4 19h16M8 16v-4M12 16V8M16 16v-6"/></svg> },
              ].map((f, i) => (
                <div key={i} className="showcase-feat">
                  <span className="feat-ico">{f.icon}</span>
                  {f.text}
                </div>
              ))}
            </div>

            <div className="showcase-foot">
              <div style={{ display: 'flex' }}>
                {['SV','RP','TL','JR'].map((ini, i) => (
                  <div key={i} className={`ds-avatar ds-avatar-${i+1}`} style={{ width:30,height:30,fontSize:11, marginLeft: i===0?0:-8, boxShadow: '0 0 0 2px var(--bg-1)' }}>{ini}</div>
                ))}
              </div>
              <div className="showcase-quote">Private, secure sign-in for <b>you and your clients</b>.</div>
            </div>
          </div>
        </section>

        {/* Right: Form */}
        <section className="auth-form-wrap">
          <div className="auth-card">
            {/* Theme toggle top right */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 40 }}>
              <button className="ds-btn ds-btn-icon ds-btn-ghost" onClick={toggleTheme} title="Toggle theme" type="button">
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19"/>
                </svg>
              </button>
            </div>

            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.025em', margin: 0, color: 'var(--text)' }}>Welcome back</h1>
              <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>{branding.tagline || `Sign in to your ${branding.cmsName} workspace.`}</p>
            </div>

            {error && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--danger-soft)', border: '1px solid transparent', color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Email */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 7 }}>Email</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)', display: 'flex', pointerEvents: 'none' }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m4 7 8 6 8-6"/></svg>
                  </span>
                  <input
                    className="ds-input"
                    type="email"
                    placeholder="you@studio.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    style={{ paddingLeft: 38, height: 42 }}
                  />
                </div>
              </div>

              {/* Password */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                  <label style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-soft)' }}>Password</label>
                  <Link to="/forgot-password" style={{ fontSize: 12.5, color: 'var(--accent-3)', textDecoration: 'none' }}>Forgot password?</Link>
                </div>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)', display: 'flex', pointerEvents: 'none' }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="11" rx="2.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
                  </span>
                  <input
                    className="ds-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    style={{ paddingLeft: 38, paddingRight: 42, height: 42 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', borderRadius: 7, border: 'none', background: 'none', cursor: 'pointer' }}
                  >
                    {showPassword
                      ? <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l18 18M10.6 6.2A9.6 9.6 0 0 1 12 5.5c6.5 0 10 6.5 10 6.5a16 16 0 0 1-3.2 3.9M6.2 7.6A16 16 0 0 0 2 12s3.5 6.5 10 6.5a9.4 9.4 0 0 0 3.6-.7M9.5 9.5a3 3 0 0 0 4.2 4.3"/></svg>
                      : <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z"/><circle cx="12" cy="12" r="2.8"/></svg>
                    }
                  </button>
                </div>
              </div>

              {/* Remember */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 22, cursor: 'pointer' }} onClick={() => {
                const next = !remember
                setRemember(next)
                localStorage.setItem('rememberStaySignedIn', next.toString())
              }}>
                <div style={{ width: 17, height: 17, borderRadius: 5, border: `1px solid ${remember ? 'transparent' : 'var(--border-strong)'}`, background: remember ? 'var(--accent)' : 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>
                  {remember && <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-10"/></svg>}
                </div>
                <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>Keep me signed in</span>
              </div>

              <button
                className="ds-btn ds-btn-primary"
                type="submit"
                disabled={loading}
                style={{ width: '100%', height: 44, fontSize: 14, fontWeight: 600, borderRadius: 11, opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Signing in…' : 'Sign in to workspace'}
                {!loading && <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>}
              </button>
            </form>

            <div style={{ marginTop: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
              <span style={{ fontSize: 11.5, color: 'var(--text-faint)', lineHeight: 1.6 }}>
                By signing in you agree to our <a href="/terms" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Terms</a> and <a href="/privacy" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Privacy Policy</a>.
              </span>
              {branding.copyrightText && (
                <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 10 }}>{branding.copyrightText}</div>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Login
